import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuthSecurity } from "@/hooks/useAuthSecurity";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, KeyRound } from "lucide-react";
import { toast } from "sonner";
import oceanBgJpg from "@/assets/ocean-bg.jpg";
import oceanBgWebp from "@/assets/ocean-bg.webp";
import seatechAppIcon from "@/assets/branding/seatech-app-icon.png";
import { getDeviceId } from "@/hooks/useDeviceId";
import { isDeviceTrackingActive } from "@/lib/trialConfig";
import { LegalFooter } from "@/components/legal/LegalFooter";

const AUTH_RETURN_TO_KEY = "auth:return_to";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    loading: authLoading,
    sessionRestored,
    webAuthnCapability,
    securityContext,
    signInWithPasskey,
  } = useAuthSecurity();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const hasConsumedReturnTargetRef = useRef(false);

  const returnTarget = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const urlTarget = params.get("return_to");
    const savedTarget = sessionStorage.getItem(AUTH_RETURN_TO_KEY);
    const rawTarget = urlTarget || savedTarget || "/home";

    try {
      const parsed = new URL(rawTarget, window.location.origin);
      if (parsed.origin !== window.location.origin) return "/home";

      if (parsed.pathname === "/emergency/rescue/session") {
        const sessionId = parsed.searchParams.get("session_id");
        if (sessionId) {
          parsed.pathname = `/emergency/rescue/session/${sessionId}`;
          parsed.searchParams.delete("session_id");
        }
      }

      const normalized = `${parsed.pathname}${parsed.search}${parsed.hash}`;
      return normalized || "/home";
    } catch {
      return "/home";
    }
  }, [location.search]);

  const clearReturnTarget = () => {
    sessionStorage.removeItem(AUTH_RETURN_TO_KEY);
    console.log("[AUTH_REDIRECT] cleared return URL key", {
      key: AUTH_RETURN_TO_KEY,
    });
  };

  const passkeyUiAvailable =
    webAuthnCapability.supported &&
    webAuthnCapability.platformAuthenticatorAvailable &&
    !!securityContext?.passkeyProjectSupported;

  useEffect(() => {
    if (!authLoading && user && !hasConsumedReturnTargetRef.current) {
      hasConsumedReturnTargetRef.current = true;

      const params = new URLSearchParams(location.search);
      const decodedQueryTarget = params.get("return_to");
      const savedTarget = sessionStorage.getItem(AUTH_RETURN_TO_KEY);

      console.log("[AUTH_REDIRECT] restored return URL after login", {
        query_return_to: decodedQueryTarget,
        saved_return_to: savedTarget,
        final_return_to: returnTarget,
      });

      clearReturnTarget();
      console.log("[AUTH_REDIRECT] final rescue route navigated to", {
        route: returnTarget,
      });
      navigate(returnTarget, { replace: true });
    }
  }, [authLoading, location.search, navigate, returnTarget, user]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isSignUp) {
        const { data: signUpData, error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin },
        });
        if (error) throw error;

        if (signUpData?.user && isDeviceTrackingActive()) {
          const deviceId = getDeviceId();
          supabase.rpc("register_device_trial", {
            p_device_id: deviceId,
            p_user_id: signUpData.user.id,
          }).then(({ error: regError }) => {
            if (regError) {
              console.error("[TRIAL_GUARD] Signup registration failed:", regError);
            }
          });
        }

        toast.success("Check your email to confirm your account");
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
      }
    } catch (error: any) {
      console.error("Auth error:", error.message);
      if (isSignUp) {
        toast.error("Unable to create account. Please try again.");
      } else {
        toast.error("Invalid email or password. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!email) {
      toast.error("Enter your email first to request a magic link.");
      return;
    }

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });

    if (error) {
      toast.error(error.message || "Unable to send magic link");
      return;
    }

    toast.success("Magic link sent. Check your inbox.");
  };

  const handlePasskeySignIn = async () => {
    const result = await signInWithPasskey();
    if (!result.ok) {
      toast.error(result.reason);
    }
  };

  if (authLoading || !sessionRestored) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
          <p className="text-sm">Restoring your session…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-8">
      <picture>
        <source srcSet={oceanBgWebp} type="image/webp" />
        <img
          src={oceanBgJpg}
          alt=""
          width={1550}
          height={1080}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          sizes="100vw"
          className="absolute inset-0 h-full w-full object-cover"
        />
      </picture>
      <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/85 to-background/95 backdrop-blur-md" />

      <div className="relative z-10 w-full max-w-md">
        <div className="glass-card rounded-2xl border border-border/40 p-7 shadow-2xl shadow-primary/5 sm:p-9">
          <div className="mb-8 flex flex-col items-center gap-4">
            <img
              src={seatechAppIcon}
              alt="Seatech"
              width={64}
              height={64}
              className="h-16 w-16 rounded-2xl object-contain shadow-lg shadow-primary/20 ring-1 ring-primary/20"
            />
            <div className="text-center leading-none">
              <h1 className="text-[1.5rem] font-semibold tracking-tight font-body">
                <span className="text-gradient-brand">Seatech</span>
                <span className="text-muted-foreground/80"> — Aquarium Intelligence</span>
              </h1>
              <p className="mt-1.5 text-sm font-medium text-muted-foreground/70 font-body">
                Aquarium emergency triage, 48-hour rescue plans, and stabilization tools.
              </p>

            </div>
          </div>

          <form onSubmit={handleAuth} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="h-11 bg-input/40 border-border/60 text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-primary/40"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete={isSignUp ? "new-password" : "current-password"}
                  className="h-11 bg-input/40 border-border/60 pr-10 text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-primary/40"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="mt-2 h-11 w-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
            >
              {isLoading ? "Please wait…" : isSignUp ? "Create account" : "Sign in"}
            </Button>

            {!isSignUp && (
              <>
                <div className="relative py-1">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border/40" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-card/80 px-2 text-[11px] uppercase tracking-wider text-muted-foreground">
                      or
                    </span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="h-11 w-full border-border/60 bg-transparent hover:bg-muted/40"
                  onClick={handleMagicLink}
                >
                  Send magic link
                </Button>

                {passkeyUiAvailable && (
                  <Button
                    type="button"
                    variant="secondary"
                    className="h-11 w-full gap-2"
                    onClick={handlePasskeySignIn}
                  >
                    <KeyRound className="h-4 w-4" />
                    Sign in with passkey
                  </Button>
                )}
              </>
            )}
          </form>

          {!isSignUp && !passkeyUiAvailable && (
            <div className="mt-5 rounded-lg border border-border/40 bg-muted/20 px-3.5 py-3">
              <p className="text-xs font-medium text-foreground/80">
                Passkeys are not available for this device or project setup yet.
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Sign in with your email or use a magic link instead.
              </p>
            </div>
          )}

          <div className="mt-7 border-t border-border/40 pt-5 text-center">
            <p className="text-sm text-muted-foreground">
              {isSignUp ? "Already have an account?" : "Don't have an account?"}{" "}
              <button
                type="button"
                onClick={() => setIsSignUp(!isSignUp)}
                className="font-semibold text-primary hover:text-primary/80 transition-colors"
              >
                {isSignUp ? "Sign in" : "Sign up"}
              </button>
            </p>
          </div>
        </div>

        <p className="mt-5 text-center text-xs text-muted-foreground/70">
          Secured by Seatech Aquarium Intelligence · Email sign-in remains available as a backup.
        </p>
        <LegalFooter className="mt-6 bg-transparent border-border/20" compact />
      </div>
    </div>
  );
};

export default Login;
